import { useMutation, useQuery } from '@tanstack/react-query'
import { mapToSku } from 'api'
import { Card, CardBody } from 'components/Card/Card'
import { CheckBox } from 'components/CheckBox/CheckBox'
import { Input } from 'components/Form'
import { JustSpinner, Spinner } from 'components/Loading/Loading'
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from 'components/ModalV2/Modal'
import { EmptyList } from 'components/Table/EmptyList'
import { useDebounce } from 'hooks/useDebounde'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { Button, Flex } from 'styling/GlobalStyles'
import { BigText, NormalText } from 'styling/TypographyV2'
import { IPaginatedResponse } from 'types/api/general.interface'
import { IInventoryItem } from 'types/inventory.interface'
import { baseUrl } from 'utils/axios'

export const MapToSkuModal = ({
  onClose,
  requestId,
  procurementPurchaseRequestItemId,
}: {
  onClose: () => void
  requestId: number | string
  procurementPurchaseRequestItemId: number
}) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)
  const [selectedItem, setSelectedItem] = useState<IInventoryItem | null>(null)

  const { data: inventoryItems, isLoading } = useQuery({
    // debounce search

    queryKey: ['inventory-items', debouncedSearch],
    queryFn: () =>
      baseUrl.get<IPaginatedResponse<IInventoryItem>>('inventory/item', {
        params: { page: 1, limit: 60, q: debouncedSearch },
      }),
  })

  const mutation = useMutation({
    mutationFn: async () => {
      if (!selectedItem) {
        throw new Error('No item selected')
      }
      const response = await mapToSku(requestId, {
        procurementPurchaseRequestItemId: procurementPurchaseRequestItemId,
        inventoryItemId: selectedItem?.id,
      })
      return response
    },
    onSuccess: () => {
      toast.success('Item mapped to SKU')
      onClose()
    },
    onError: (error) => {
      console.log(error)
      toast.error(error.message)
    },
  })

  return (
    <Modal
      onClose={() => {
        onClose()
      }}
    >
      <ModalContent>
        <ModalHeader>
          <BigText>Select Item to map to SKU</BigText>
        </ModalHeader>
        <ModalBody>
          <Input
            placeholder='Search for an item'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Flex column gap='0.5rem' mt='16' style={{ minHeight: '200px' }} center>
            {isLoading ? (
              <Flex center justcenter height='200px'>
                <JustSpinner />
              </Flex>
            ) : inventoryItems?.data?.data?.length > 0 ? (
              inventoryItems?.data?.data?.map((item) => (
                <Card key={item.id}>
                  <CardBody>
                    <CheckBox
                      state={selectedItem?.id === item.id}
                      square
                      fit
                      title={item.name}
                      onClick={() => {
                        setSelectedItem(item)
                      }}
                    />
                  </CardBody>
                </Card>
              ))
            ) : (
              <EmptyList maxHeight='200px' />
            )}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Flex column gap='0.5rem' width='100%'>
            <Flex gap='1rem' width='100%'>
              <Button width100 onClick={onClose}>
                Cancel
              </Button>
              <Button width100 orange onClick={() => mutation.mutate()}>
                Select
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <Spinner loading={mutation.isPending} />
    </Modal>
  )
}
